import React, { useState } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { pluralizeToString } from '~/utils/stringUtils';

import type { CopySectionDetails } from '../copyFromPrevious/copyFromPreviousModel';

import { type ImportSectionKey, type ImportSectionStatus, noteTexts } from './excelImportModel';

export type ImportSectionRow = {
  key: ImportSectionKey;
  label: string;
  status: ImportSectionStatus;
  line: string;
  notes: CopySectionDetails['notes'];
  waiting: boolean;
};

type ImportSectionTableProps = {
  rows: Array<ImportSectionRow>;
  selected: Set<ImportSectionKey>;
  onToggle: (section: ImportSectionKey, checked: boolean) => void;
};

const LINE_COLOR: Record<ImportSectionStatus, string> = {
  loads: 'text.primary',
  blocked: 'error',
  empty: 'text.secondary',
};

export default function ImportSectionTable({ rows, selected, onToggle }: ImportSectionTableProps) {
  const [openSection, setOpenSection] = useState<ImportSectionKey | null>(null);
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">Load</TableCell>
          <TableCell sx={{ width: 200 }}>Section</TableCell>
          <TableCell>From the workbook</TableCell>
          <TableCell sx={{ width: 160 }}>Notes</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const warnings = noteTexts(row.notes, 'warning');
          const infos = noteTexts(row.notes, 'info');
          const counts = [
            ...(warnings.length ? [pluralizeToString('warning', warnings.length)] : []),
            ...(infos.length ? [pluralizeToString('note', infos.length)] : []),
          ];
          const isOpen = openSection === row.key && row.notes.length > 0;
          // The notes sit on their own line under the section, read as part of the same row.
          const joined = isOpen ? { borderBottom: 'none' } : {};
          return (
            <React.Fragment key={row.key}>
              <TableRow>
                <TableCell padding="checkbox" sx={joined}>
                  <Checkbox
                    checked={selected.has(row.key)}
                    disabled={row.status !== 'loads'}
                    inputProps={{ 'aria-label': row.label }}
                    onChange={(event) => onToggle(row.key, event.target.checked)}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', ...joined }}>{row.label}</TableCell>
                <TableCell sx={joined}>
                  <Stack alignItems="center" direction="row" spacing={1}>
                    {row.waiting && <CircularProgress size={16} />}
                    <Typography
                      color={
                        row.status === 'loads' && !selected.has(row.key) ? 'text.secondary' : LINE_COLOR[row.status]
                      }
                      variant="body2"
                    >
                      {row.line}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', ...joined }}>
                  {counts.length > 0 && (
                    <Button
                      endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      onClick={() => setOpenSection(isOpen ? null : row.key)}
                      size="small"
                      variant="text"
                    >
                      {counts.join(', ')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
              {isOpen && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ pt: 0 }}>
                    <Stack spacing={1}>
                      {[
                        { severity: 'warning' as const, texts: warnings },
                        { severity: 'info' as const, texts: infos },
                      ].map(
                        ({ severity, texts }) =>
                          texts.length > 0 && (
                            <Alert key={severity} severity={severity}>
                              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {texts.map((text, index) => (
                                  <li key={index}>{text}</li>
                                ))}
                              </Box>
                            </Alert>
                          ),
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
